import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useDebounce } from "@reactuses/core";
import { useTranslation } from "react-i18next";
import { useDateFormatter } from "../../../../i18n/useDateFormatter";
import { PageContainer } from "../../../../components/core/basic/PageContainer";
import BreadcrumbDates from "../../../../components/core/blocks/BreadcrumbDates";
import MetricsCard from "../../../../components/custom/merchandise/MetricsCard";
import { usePersistedTableState } from "../../../../components/table/usePersistedTableState";
import { idsToVisibilityState, selectionStateToSelectedIds } from "../../../../components/table/utils";
import { DatesProvider, useDates } from "../../../../dates/DatesContext";
import { useModuleEnabled } from "../../../../redux/selectors/modules";
import { useAcosOrRoas } from "../../../../utils/acosOrRoas";
import { ORG_SUMMARY_REPORT_BREADCRUMB_SPEC, REPORT_LINKS } from "../../../../utils/dashboardLinks";
import getMetricSpec from "../../../../modules/merchandising/utils/metrics";
import { useGetV4AdvReportByDate } from "../../../../modules/perf_reports/api/ads/v4AdvReportByDate";
import { useGetV4AdvReportTotals } from "../../../../modules/perf_reports/api/ads/v4AdvReportTotals";
import { useStandardEntityMetrics } from "../../../../modules/merchandising/components/tables/metricUtils";
import { AdReportTable } from "../../../../modules/perf_reports/ad_report/AdReportTable";
import useAdReportColumns from "../../../../modules/perf_reports/ad_report/useAdReportColumns";
import { Accordion } from "../../../../components/core/basic/Accordion";
import { ArchivedsAdsTable } from "../../../../modules/perf_reports/ad_report/ArchivedAdsTable";
import { useAppSelector } from "../../../../redux/hooks";
import { KDP_AUTHOR } from "../../../../utils/profileSubtypes";

function AdvertisingReportBody() {
  const { t } = useTranslation(["perf_reports"]);
  const acosOrRoas = useAcosOrRoas();
  const user = useAppSelector((state) => state.user);
  const hasArchivedAdsModuleEnabled = useModuleEnabled("ARCHIVED_ADS");
  const standardMetricIds = useStandardEntityMetrics("mediaPlan");
  const [quickFilterValue, setQuickFilterValue] = useState("all");
  const [adType, setAdType] = useState<string | number | null>("all");
  const columns = useAdReportColumns();

  const { start, end, group } = useDates();
  const longDate = useDateFormatter("long");
  const defaultSelectedColumns = [
    "state",
    "created_datetime",
    "target_acos",
    "max_acos",
    "daily_budget",
    "ad_sales",
    "ad_spend",
    "acos",
    ...(user.organization_subtype === KDP_AUTHOR ? ["kenp_royalties", "acos_with_kenp"] : []),
    "roas",
    "orders",
    "impressions",
    "clicks",
    "ctr",
    "conversion_rate",
    "cpc",
    "serving_hours",
  ];
  const defaultHiddenCols = idsToVisibilityState(
    defaultSelectedColumns,
    columns.filter((column) => Boolean(column.id)).map((column) => column.id!),
  );
  const tableInitialState = {
    columnVisibility: {
      ...defaultHiddenCols,
      acos: acosOrRoas(true, false),
      roas: acosOrRoas(false, true),
    },
    columnOrder: defaultSelectedColumns,
    columnPinning: {
      start: ["ad_name"],
      end: [],
    },
    sorting: [{ id: "ad_sales", desc: true }],
  };
  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "adReport",
    initialState: tableInitialState,
  });
  const {
    setters: archivedSetters,
    state: archivedState,
    initialState: archivedInitialState,
  } = usePersistedTableState({
    persistKey: "archivedAdReport",
    initialState: tableInitialState,
  });

  const debounceSearch = useDebounce(state.globalFilter, 500);
  const selectedIds = selectionStateToSelectedIds(state.rowSelection);

  const filters = useMemo(() => {
    const selectedIdsFilter =
      selectedIds.length > 0 ? [{ id: "ad_id", value: { op: "anyof", value: selectedIds } }] : [];
    const adTypeFilter = adType !== "all" ? [{ id: "ad_type", value: { op: "=", value: adType } }] : [];
    const quickFilter = quickFilterValue !== "all" ? [{ id: quickFilterValue, value: { op: "=", value: true } }] : [];
    return [...state.columnFilters, ...selectedIdsFilter, ...adTypeFilter, ...quickFilter];
  }, [state.columnFilters, selectedIds, adType, quickFilterValue]);

  const byDateOptionsGetter = useGetV4AdvReportByDate();
  const advByDateReportQuery = useQuery(
    byDateOptionsGetter({
      filters: filters,
      search: debounceSearch,
    }),
  );
  const totalsOptionsGetter = useGetV4AdvReportTotals();
  const advTotalsReportQuery = useQuery(
    totalsOptionsGetter({
      filters: filters,
      search: debounceSearch,
    }),
  );

  const timeLineData = useMemo(() => {
    return advByDateReportQuery.data?.data ?? [];
  }, [advByDateReportQuery.data?.data]);

  const metrics = useMemo(() => {
    return standardMetricIds.map((id) => getMetricSpec(id));
  }, [standardMetricIds]);

  return (
    <PageContainer withBreadcrumb>
      <MetricsCard
        id={"adReport"}
        title={t(($) => $.perf_reports["Performance Overview"])}
        totals={{
          data: advTotalsReportQuery.data?.data,
          loading:
            advTotalsReportQuery.isFetching && (!advTotalsReportQuery.data || advTotalsReportQuery.isPlaceholderData),
        }}
        timeline={{
          data: timeLineData,
          loading:
            advByDateReportQuery.isFetching && (!advByDateReportQuery.data || advByDateReportQuery.isPlaceholderData),
        }}
        metrics={metrics}
        showMetricsSelector
        initialSelectedMetric={["ad_sales", "ad_spend", acosOrRoas("acos", "roas")]}
        group={group}
        showAnnotations={true}
        byline={
          <>
            {longDate.format(start.toDate())}
            {" - "}
            {longDate.format(end.toDate())}
          </>
        }
        context={"adReport"}
      >
        <AdReportTable
          state={state}
          initialState={initialState}
          setters={setters}
          quickFilterValue={quickFilterValue}
          setQuickFilterValue={setQuickFilterValue}
          adType={adType}
          setAdType={setAdType}
          additionalFilters={{
            adType: String(adType),
          }}
          columns={columns}
        />
        {user.is_staff && hasArchivedAdsModuleEnabled && (
          <Accordion title={t(($) => $.perf_reports["Archived Media Plans"])}>
            <ArchivedsAdsTable
              state={archivedState}
              initialState={archivedInitialState}
              setters={archivedSetters}
              viewKey={"archivedAdReport"}
              quickFilterValue={quickFilterValue}
              adType={adType}
              setAdType={setAdType}
              additionalFilters={{
                adType: String(adType),
              }}
              columns={columns}
            />
          </Accordion>
        )}
      </MetricsCard>
    </PageContainer>
  );
}

function AdvertisingReport() {
  const { t } = useTranslation(["perf_reports", "common"]);
  const hasOrgSummaryreportModule = useModuleEnabled("ORG_SUMMARY_REPORT");
  const breadCrumbItems = [
    {
      name: t(($) => $.perf_reports["Advertisement Report"]),
      href: "/user/report/advertisement",
      dropdownLinkOptions: REPORT_LINKS,
      searchable: false,
      dropdownSubtitle: t(($) => $.common.sidebar["Reports"]),
    },
  ];

  if (
    hasOrgSummaryreportModule &&
    !breadCrumbItems[0]?.dropdownLinkOptions.find((o) => o.name === ORG_SUMMARY_REPORT_BREADCRUMB_SPEC.name)
  ) {
    breadCrumbItems[0]?.dropdownLinkOptions.unshift(ORG_SUMMARY_REPORT_BREADCRUMB_SPEC);
  }
  return (
    <DatesProvider>
      <BreadcrumbDates items={breadCrumbItems} />
      <AdvertisingReportBody />
    </DatesProvider>
  );
}

export const Route = createFileRoute("/_authenticated/user/report/advertisement")({
  component: AdvertisingReport,
  staticData: { application: "Reports" },
});
